import { useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard } from '../api/guard'
import './screens.css'

interface Membership {
  workspace_id: string
  workspace_name: string | null
  role: string
}
interface AdminUser {
  id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  created_at: string | null
  last_sign_in_at: string | null
  memberships: Membership[]
}
interface UsersResponse {
  users: AdminUser[]
  page: number
  per_page: number
  has_more: boolean
}

const PER_PAGE = 50

function fullName(u: AdminUser): string {
  const n = [u.first_name, u.last_name].filter(Boolean).join(' ')
  return n || '—'
}

export function UsersView() {
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState('')

  const query = useQuery<UsersResponse>({
    queryKey: ['admin-users', page],
    queryFn: () =>
      apiFetch<UsersResponse>(`/admin/users?page=${page}&per_page=${PER_PAGE}`),
    placeholderData: keepPreviousData,
  })
  useForbiddenGuard(query.error)

  const users = query.data?.users ?? []
  const hasMore = query.data?.has_more ?? false
  const needle = filter.trim().toLowerCase()
  const rows = needle
    ? users.filter(
        (u) =>
          (u.email ?? '').toLowerCase().includes(needle) ||
          fullName(u).toLowerCase().includes(needle),
      )
    : users

  return (
    <section className="scr" aria-labelledby="users-heading">
      <div className="screen-head">
        <h2 id="users-heading">Users</h2>
        <input
          className="ws-search"
          type="search"
          aria-label="Filter this page of users"
          placeholder="Filter this page…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load users.
        </p>
      )}

      <table className="scr-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Memberships</th>
            <th>Created</th>
            <th>Last sign-in</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id}>
              <td>{u.email ?? '—'}</td>
              <td>{fullName(u)}</td>
              <td>
                {u.memberships.length === 0 ? (
                  <span className="scr-muted">none</span>
                ) : (
                  <span className="scr-chips">
                    {u.memberships.map((m) => (
                      <span key={m.workspace_id} className="scr-chip">
                        {m.workspace_name ?? m.workspace_id.slice(0, 8)} · {m.role}
                      </span>
                    ))}
                  </span>
                )}
              </td>
              <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
              <td>
                {u.last_sign_in_at
                  ? new Date(u.last_sign_in_at).toLocaleDateString()
                  : '—'}
              </td>
            </tr>
          ))}
          {!query.isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={5} className="ws-empty">
                {needle ? 'No users match this filter on the current page.' : 'No users.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="ws-pager">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </button>
        <span className="ws-count">Page {page}</span>
        <button type="button" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </div>
      <p className="scr-note">
        The filter box narrows the current page only. Use Previous/Next to page through all users.
      </p>
    </section>
  )
}
